import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { reviewSchema, type ReviewCreateInput } from "@ckgo2/shared/schemas";
import { api, type ApiResponse } from "@/api/client";
import type { Trip, Guide, Destination } from "@ckgo2/shared/types";
import { ArrowLeft, Save, Loader2, Star } from "lucide-react";

interface Review {
  id: string;
  tripId?: string;
  destinationId?: string;
  guideId?: string;
  customerName: string;
  customerLocation?: string;
  rating: number;
  title?: string;
  content: string;
  tripDate?: string;
  isFeatured: boolean;
  isVideo: boolean;
  videoUrl?: string;
  isApproved: boolean;
}

export default function ReviewEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ReviewCreateInput>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      customerName: "",
      customerLocation: "",
      rating: 5,
      title: "",
      content: "",
      tripId: "",
      guideId: "",
      destinationId: "",
      isFeatured: false,
      isVideo: false,
      videoUrl: "",
      isApproved: false,
    },
  });

  // Fetch review if editing
  const { data: review, isLoading } = useQuery({
    queryKey: ["review", id],
    queryFn: () => api.get<ApiResponse<Review>>(`/api/admin/reviews/${id}`),
    enabled: !isNew,
  });

  // Fetch trips for select
  const { data: tripsData } = useQuery({
    queryKey: ["trips"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/trips"),
  });

  // Fetch guides for select
  const { data: guidesData } = useQuery({
    queryKey: ["guides"],
    queryFn: () => api.get<ApiResponse<Guide[]>>("/api/guides"),
  });

  // Fetch destinations for select
  const { data: destinationsData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/destinations"),
  });

  const trips = tripsData?.data || [];
  const guides = guidesData?.data || [];
  const destinations = destinationsData?.data || [];

  // Reset form when review data loads
  useEffect(() => {
    if (review?.data) {
      reset({
        ...review.data,
        tripId: review.data.tripId || "",
        guideId: review.data.guideId || "",
        destinationId: review.data.destinationId || "",
        tripDate: review.data.tripDate ? new Date(review.data.tripDate) : undefined,
      });
    }
  }, [review, reset]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: ReviewCreateInput) => {
      // Clean up empty strings to undefined
      const cleanData = {
        ...data,
        tripId: data.tripId || undefined,
        guideId: data.guideId || undefined,
        destinationId: data.destinationId || undefined,
        videoUrl: data.videoUrl || undefined,
      };

      if (isNew) {
        return api.post<ApiResponse<Review>>("/api/admin/reviews", cleanData);
      }
      return api.put<ApiResponse<Review>>(`/api/admin/reviews/${id}`, cleanData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
      queryClient.invalidateQueries({ queryKey: ["review", id] });
      navigate("/reviews");
    },
  });

  const onSubmit = async (data: ReviewCreateInput) => {
    await saveMutation.mutateAsync(data);
  };

  const rating = watch("rating");
  const isVideo = watch("isVideo");

  if (!isNew && isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/reviews"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "Nová recenze" : "Upravit recenzi"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew
                ? "Vytvořte novou recenzi"
                : `Úprava recenze od ${review?.data?.customerName}`}
            </p>
          </div>
        </div>

        <button
          type="submit"
          form="review-form"
          disabled={isSubmitting || (!isDirty && !isNew)}
          className="btn-primary"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Ukládám...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Uložit
            </>
          )}
        </button>
      </div>

      {/* Error message */}
      {saveMutation.error && (
        <div className="rounded-lg bg-error-50 p-4 text-error-700 dark:bg-error-900/30 dark:text-error-400">
          {saveMutation.error.message}
        </div>
      )}

      {/* Form */}
      <form
        id="review-form"
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-6 lg:grid-cols-3"
      >
        {/* Main content - 2 columns */}
        <div className="space-y-6 lg:col-span-2">
          {/* Customer info */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Informace o zákazníkovi
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Customer Name */}
              <div>
                <label htmlFor="customerName" className="label">
                  Jméno zákazníka *
                </label>
                <input
                  {...register("customerName")}
                  type="text"
                  id="customerName"
                  className={`input ${errors.customerName ? "input-error" : ""}`}
                  placeholder="např. Jan Novák"
                />
                {errors.customerName && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.customerName.message}
                  </p>
                )}
              </div>

              {/* Customer Location */}
              <div>
                <label htmlFor="customerLocation" className="label">
                  Město/Lokalita
                </label>
                <input
                  {...register("customerLocation")}
                  type="text"
                  id="customerLocation"
                  className="input"
                  placeholder="např. Praha"
                />
              </div>
            </div>
          </div>

          {/* Review content */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Obsah recenze
            </h2>

            <div className="space-y-4">
              {/* Rating */}
              <div>
                <label className="label">Hodnocení *</label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setValue("rating", value, { shouldDirty: true })}
                      className="p-1"
                    >
                      <Star
                        className={`h-8 w-8 transition-colors ${
                          value <= rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300 hover:text-yellow-300"
                        }`}
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-gray-500">
                    {rating} z 5
                  </span>
                </div>
              </div>

              {/* Title */}
              <div>
                <label htmlFor="title" className="label">
                  Titulek recenze
                </label>
                <input
                  {...register("title")}
                  type="text"
                  id="title"
                  className="input"
                  placeholder="např. Nezapomenutelný zážitek!"
                />
              </div>

              {/* Content */}
              <div>
                <label htmlFor="content" className="label">
                  Text recenze *
                </label>
                <textarea
                  {...register("content")}
                  id="content"
                  rows={6}
                  className={`input ${errors.content ? "input-error" : ""}`}
                  placeholder="Napište recenzi zákazníka..."
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.content.message}
                  </p>
                )}
              </div>

              {/* Trip Date */}
              <div>
                <label htmlFor="tripDate" className="label">
                  Datum cesty
                </label>
                <input
                  {...register("tripDate", { valueAsDate: true })}
                  type="date"
                  id="tripDate"
                  className="input"
                />
              </div>
            </div>
          </div>

          {/* Video */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Video recenze
            </h2>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  {...register("isVideo")}
                  type="checkbox"
                  id="isVideo"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label
                  htmlFor="isVideo"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Toto je video recenze
                </label>
              </div>

              {isVideo && (
                <div>
                  <label htmlFor="videoUrl" className="label">
                    URL videa
                  </label>
                  <input
                    {...register("videoUrl")}
                    type="url"
                    id="videoUrl"
                    className="input"
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Relations */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Propojení
            </h2>

            <div className="space-y-4">
              {/* Trip */}
              <div>
                <label htmlFor="tripId" className="label">
                  Zájezd
                </label>
                <select {...register("tripId")} id="tripId" className="input">
                  <option value="">-- Vyberte zájezd --</option>
                  {trips.map((trip) => (
                    <option key={trip.id} value={trip.id}>
                      {trip.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Guide */}
              <div>
                <label htmlFor="guideId" className="label">
                  Průvodce
                </label>
                <select {...register("guideId")} id="guideId" className="input">
                  <option value="">-- Vyberte průvodce --</option>
                  {guides.map((guide) => (
                    <option key={guide.id} value={guide.id}>
                      {guide.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Destination */}
              <div>
                <label htmlFor="destinationId" className="label">
                  Destinace
                </label>
                <select {...register("destinationId")} id="destinationId" className="input">
                  <option value="">-- Vyberte destinaci --</option>
                  {destinations.map((dest) => (
                    <option key={dest.id} value={dest.id}>
                      {dest.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Nastavení
            </h2>

            <div className="space-y-4">
              {/* Is Approved */}
              <div className="flex items-center gap-3">
                <input
                  {...register("isApproved")}
                  type="checkbox"
                  id="isApproved"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label
                  htmlFor="isApproved"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Schváleno k zobrazení
                </label>
              </div>

              {/* Is Featured */}
              <div className="flex items-center gap-3">
                <input
                  {...register("isFeatured")}
                  type="checkbox"
                  id="isFeatured"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label
                  htmlFor="isFeatured"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Zobrazit jako featured
                </label>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
